/**
 * ContractEditorAccessPage
 * 
 * Public page for accessing contract editor via email link.
 * No authentication required - uses access token from URL.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ContractEditor } from '@/components/contract-builder/ContractEditor';
import { AIChatSidebar } from '@/components/contract-builder/AIChatSidebar';
import { Button } from '@/components/ui/button';
import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';
import { Download, FileText, Save, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import PublicNavBar from '@/components/layout/PublicNavBar';
import { Loader2 } from 'lucide-react';

export const ContractEditorAccessPage: React.FC = () => {
  const { accessToken } = useParams<{ accessToken: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [contractId, setContractId] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [structuredData, setStructuredData] = useState<any>(null);
  const [userEmail, setUserEmail] = useState('');
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    if (accessToken) {
      loadContractFromToken();
    }
  }, [accessToken]);

  const loadContractFromToken = async () => {
    if (!accessToken) {
      setError('No access token provided');
      setIsLoading(false);
      return;
    }

    try {
      const response = await ApiService.request<{
        contract_id: string;
        html_content: string;
        structured_data: any;
        status: string;
        user_email: string;
        user_name: string;
      }>(EndpointKeys.CONTRACT_BUILDER_ACCESS_EDITOR, {
        method: 'GET',
        urlParams: { token: accessToken },
        skipAuth: true, // This endpoint uses token auth, not session auth
      });

      setContractId(response.contract_id);
      setHtmlContent(response.html_content);
      setStructuredData(response.structured_data);
      setUserEmail(response.user_email);
      setUserName(response.user_name);
      setIsLoading(false);
    } catch (error: any) {
      console.error('Failed to load contract:', error);
      setError(error.message || 'Invalid or expired access link');
      setIsLoading(false);
    }
  };

  const handleAIEdit = (updatedHtml: string, changes: string[]) => {
    setHtmlContent(updatedHtml);
  };

  const handleContentChange = (html: string) => {
    setHtmlContent(html);
  };

  const handleSave = async () => {
    try {
      await ApiService.request(EndpointKeys.CONTRACT_BUILDER_SAVE, {
        method: 'POST',
        urlParams: { contractId },
        body: { 
          html_content: htmlContent,
          structured_data: structuredData,
          user_email: userEmail,
        },
        skipAuth: true,
      });

      toast({
        title: 'Saved',
        description: 'Your contract has been saved successfully.',
      });
    } catch (error) {
      console.error('Failed to save contract:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to save contract. Please try again.',
      });
    }
  };

  const handleDownload = async () => {
    try {
      const response = await ApiService.request<{ pdf_url: string }>(
        EndpointKeys.CONTRACT_BUILDER_FINALIZE,
        {
          method: 'POST',
          urlParams: { contractId },
          body: { html_content: htmlContent },
          skipAuth: true,
        }
      );

      // Open the PDF URL in a new tab
      window.open(response.pdf_url, '_blank');

      toast({
        title: 'Success',
        description: 'Your contract PDF is ready for download.',
      });
    } catch (error) {
      console.error('Failed to download contract:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to generate PDF. Please try again.',
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <>
        <PublicNavBar />
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
            <p className="text-gray-600">Loading your contract...</p>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <PublicNavBar />
        <div className="flex h-screen items-center justify-center">
          <div className="max-w-md text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Unable to Load Contract
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/contract-builder')}>
              Create New Contract
            </Button>
          </div>
        </div>
      </>
    );
  }

  // Main editor interface
  return (
    <>
      <PublicNavBar />
      <div className="h-screen flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Employment Contract Editor
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Welcome back, {userName}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Save Draft
            </Button>

            <Button
              variant="default"
              size="sm"
              onClick={handleDownload}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
          </div>
        </header>

        {/* Main interface */}
        <div className="flex-1 flex overflow-hidden">
          {/* Editor (left 60%) */}
          <div className="flex-[6] overflow-auto bg-white border-r border-gray-200">
            <ContractEditor
              initialHtml={htmlContent}
              onContentChange={handleContentChange}
              isLocked={isAIProcessing}
            />
          </div>

          {/* AI Chat (right 40%) */}
          <div className="flex-[4] overflow-hidden">
            <AIChatSidebar
              contractId={contractId}
              currentHtml={htmlContent}
              onAIEdit={handleAIEdit}
              isProcessing={isAIProcessing}
              setIsProcessing={setIsAIProcessing}
            />
          </div>
        </div>
      </div>
    </>
  );
};

